import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SocialButton } from "@/components/ui/social-button";
import { FormDivider } from "@/components/ui/form-divider";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect, useRef } from "react";
import { useToast } from "@/features/toast";
import useAuthStore from "@/store/use-auth-store";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  EyeIcon,
  EyeOffIcon,
  CheckCircle2,
  XCircle,
  User,
  Mail,
  Lock,
} from "lucide-react";
import {
  GoogleBrandIcon,
  AppleBrandIcon,
} from "@/components/icons/social-icons";
import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import { AuthStage } from "@/components/auth/auth-stage";
import {
  authFieldClass,
  authFooterClass,
  authFormClass,
  authLabelClass,
  authLinkClass,
  authPrimaryButtonClass,
  authSocialStackClass,
  authStackClass,
  authSubtitleClass,
  authTitleClass,
} from "@/components/auth/auth-ui";
import { AuthSupportLink } from "@/components/auth/auth-support-link";
import {
  PasswordRequirements,
  areAllPasswordRequirementsMet,
} from "@/components/auth/password-requirements";
import { miniAppBridgeService } from "@/api/services";
import mixpanel from "mixpanel-browser";
import { track } from "@/lib/analytics";
import * as Sentry from "@sentry/react";
import { AgreementViewModal, useAgreementsStore } from "@/features/agreements";
import { useAppleSignIn } from "@/hooks";

// Form verilerini localStorage'da saklamak için key
const REGISTER_FORM_KEY = "harmonyai_register_form";

// Form data interface
interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

// Form verilerini kaydetme
const saveFormData = (data: RegisterFormData) => {
  try {
    localStorage.setItem(REGISTER_FORM_KEY, JSON.stringify(data));
  } catch (errorResponse) {
    console.error("Form verilerini kaydetme hatası:", errorResponse);
  }
};

// Form verilerini yükleme
const loadFormData = (): RegisterFormData | null => {
  try {
    const data = localStorage.getItem(REGISTER_FORM_KEY);
    return data ? JSON.parse(data) : null;
  } catch (errorResponse) {
    console.error("Form verilerini yükleme hatası:", errorResponse);
    return null;
  }
};

// Form verilerini temizleme
const clearFormData = () => {
  try {
    localStorage.removeItem(REGISTER_FORM_KEY);
  } catch (errorResponse) {
    console.error("Form verilerini temizleme hatası:", errorResponse);
  }
};

// Backend'den dönen register response tipi
interface RegisterResponse {
  success: boolean;
  message: string;
  code?: string;
  data?: {
    _id: string;
    name: string;
    email: string;
    image: string;
    isVerified: boolean;
    role: string;
    token: number;
    theme: string;
    language: string;
  };
}

export default function RegisterPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isMiniAppUser, setIsMiniAppUser] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { success, error } = useToast();
  const { openViewModal } = useAgreementsStore();
  const { signIn: handleAppleRegister, isLoading: isAppleLoading } =
    useAppleSignIn("register");
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const isSubmitting = useRef(false);

  // MiniApp state from navigation
  const miniAppState = location.state as {
    fromMiniApp?: boolean;
    email?: string;
  } | null;

  // Component mount olduğunda kayıtlı form verilerini yükle
  useEffect(() => {
    const savedFormData = loadFormData();
    if (savedFormData) {
      setName(savedFormData.name || "");
      setEmail(savedFormData.email || "");
      setPassword(savedFormData.password || "");
      setConfirmPassword(savedFormData.confirmPassword || "");
    }
  }, []);

  // MiniApp integration - fill fields from shell app
  useEffect(() => {
    const handleMiniAppRegister = async () => {
      // Check if we're in a miniapp environment
      if (!miniAppBridgeService.isInMiniApp()) {
        return;
      }

      try {
        // Get user identity and email from shell app
        const [identity, shellEmail] = await Promise.all([
          miniAppBridgeService.getUserIdentity(),
          miniAppBridgeService.getUserEmail(),
        ]);

        // Fill in the fields
        if (identity) {
          const fullName = `${identity.Name || ""} ${
            identity.Surname || ""
          }`.trim();
          if (fullName) {
            setName(fullName);
          }
        }

        // Use email from navigation state if available, otherwise from shell
        const emailToUse = miniAppState?.email || shellEmail;
        if (emailToUse) {
          setEmail(emailToUse);
        }

        // Mark as miniapp user to show message
        setIsMiniAppUser(true);

        // Focus on password field after a short delay
        setTimeout(() => {
          passwordInputRef.current?.focus();
        }, 100);
      } catch (err) {
        console.error("MiniApp: Failed to get user data:", err);
      }
    };

    handleMiniAppRegister();
  }, [miniAppState?.email]);

  // Form verilerinde değişiklik olduğunda kaydet
  useEffect(() => {
    // Sadece gerçek kullanıcı inputu varsa kaydet (boş değerler değil)
    if (name || email || password || confirmPassword) {
      const formData: RegisterFormData = {
        name,
        email,
        password,
        confirmPassword,
      };
      saveFormData(formData);
    }
  }, [name, email, password, confirmPassword]);

  // Auth durumu değiştiğinde form verilerini temizle
  const user = useAuthStore((state) => state.user);
  useEffect(() => {
    if (user) {
      clearFormData();
    }
  }, [user]);

  const isFormValid = () => {
    return (
      name.trim() !== "" &&
      email.trim() !== "" &&
      areAllPasswordRequirementsMet(password) &&
      password === confirmPassword &&
      confirmPassword !== "" &&
      termsAccepted
    );
  };

  const handleGoogleRegister = async () => {
    try {
      setIsLoading(true);

      // Backend OAuth endpoint'ine yönlendir
      window.location.href = `${import.meta.env.VITE_API_BASE_URL}/auth/google`;
    } catch (errorCatch: any) {
      console.error("Google register error:", errorCatch);
      error(errorCatch.message || t("error.somethingWentWrong"), {
        title: t("auth.registerError"),
      });
      setIsLoading(false);
    }
  };

  // const handleFacebookRegister = async () => {
  //   setIsLoading(true);
  //   setTimeout(() => {
  //     setIsLoading(false);
  //     info(t("auth.featureComingSoon"), {
  //       title: t("auth.notImplemented"),
  //     });
  //   }, 1000);
  // };

  const handleCredentialsRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid() || isSubmitting.current) return;

    if (password !== confirmPassword) {
      error(t("auth.passwordsDoNotMatch"), {
        title: t("auth.registerError"),
      });
      return;
    }

    isSubmitting.current = true;
    setIsLoading(true);

    try {
      const response = await apiClient.post<RegisterResponse>(
        API_ENDPOINTS.register,
        {
          email,
          password,
          name,
          agreementName: "terms-of-use",
        },
      );

      // ✅ CRITICAL: apiClient 4xx'leri de resolve ettiği için, sadece "response var" kontrolü yeterli değil.
      // Success false ise doğrulama adımına geçmemeliyiz.
      if (!response?.success) {
        const errorCode = response?.code;
        const fallbackMessage =
          response?.message || t("error.somethingWentWrong");

        const messageByCode: Record<string, string> = {
          user_already_exists: t("auth.errors.userAlreadyExists"),
          email_not_whitelisted: t("auth.errors.emailNotWhitelisted"),
          password_required: t("auth.errors.passwordRequired"),
          terms_of_use_not_found: t("auth.errors.termsOfUseNotFound"),
          free_subscription_creation_failed: t(
            "auth.errors.freeSubscriptionCreationFailed",
          ),
        };

        error(messageByCode[errorCode || ""] || fallbackMessage, {
          title: t("auth.registerError"),
        });
        return;
      }

      // Form verilerini temizle (sadece başarılı register sonrası)
      clearFormData();

      const responseUser = response.data;
      if (responseUser) {
        Sentry.setUser({ id: responseUser._id, email: responseUser.email });
        mixpanel.alias(responseUser._id);
        mixpanel.identify(responseUser._id);
        mixpanel.people.set({
          $name: responseUser.name,
          $email: responseUser.email,
          $created: new Date().toISOString(),
        });
        // GA4 tarafında olayın adı `sign_up`; dataLayer'a e-posta ve ad
        // geçmiyoruz — kişisel veri GA4'e gönderilemez.
        track(
          "user_registered",
          {
            type: "credentials_register",
            email: responseUser.email,
            name: responseUser.name,
            _id: responseUser._id,
          },
          {
            dataLayerEvent: "sign_up",
            dataLayerProps: {
              method: "credentials",
              user_id: responseUser._id,
            },
          },
        );
        window.netmera.push((api) => {
          const user = api.getUser();
          user.setName(responseUser.name);
          user.setEmail(responseUser.email);
          user.setCustomId(responseUser._id);
          user.save();

          api.sendEvent({
            code: "n:rg",
            uid: responseUser._id,
          });
        });
      }

      success(t("auth.verificationCodeSent", { email }), {
        title: t("auth.registerSuccess"),
        duration: 10000,
      });
      navigate("/verify-email", { state: { email } });
    } catch (errorCatch: any) {
      console.error("Register error:", errorCatch);
      error(
        errorCatch.response?.data?.message ||
          errorCatch.message ||
          t("error.somethingWentWrong"),
        {
          title: t("auth.registerError"),
        },
      );
    } finally {
      isSubmitting.current = false;
      setIsLoading(false);
    }
  };

  return (
    <AuthStage>
          <div className={authStackClass}>
            {/* Header */}
            <div className="flex flex-col items-start gap-1.5">
              <h1 className={authTitleClass}>
                {t("auth.register")}
              </h1>
              <div className={authSubtitleClass}>
                <span>{t("auth.alreadyHaveAccount")}</span>
                <Link
                  to="/login"
                  className={authLinkClass}
                >
                  {t("auth.login")}
                </Link>
              </div>
            </div>

            {/* MiniApp Message */}
            {isMiniAppUser && (
              <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-sm text-foreground">
                  {t("auth.miniappRegisterMessage")}
                </p>
              </div>
            )}

            {/* Social Buttons - Hidden in MiniApp */}
            {!miniAppBridgeService.isInMiniApp() && (
              <>
                <div className={authSocialStackClass}>
                  <SocialButton
                    onClick={handleGoogleRegister}
                    icon={<GoogleBrandIcon className="h-5 w-5" />}
                    label={t("auth.signUpWithGoogle")}
                    visuallyDisabled={isLoading}
                  />
                  {import.meta.env.VITE_APP_ENV === "dev" && (
                    <SocialButton
                      onClick={handleAppleRegister}
                      icon={<AppleBrandIcon className="h-5 w-5" />}
                      label={t("auth.signUpWithApple")}
                      visuallyDisabled={isLoading || isAppleLoading}
                    />
                  )}
                </div>

                <FormDivider text={t("common.or")} />
              </>
            )}

            {/* Form */}
            <form onSubmit={handleCredentialsRegister} className={authFormClass}>
              <div className="space-y-1">
                <label
                  htmlFor="name"
                  className={authLabelClass}
                >
                  {t("auth.name")}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className={`${authFieldClass} w-full pl-9`}
                    placeholder={t("auth.enterName")}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="email"
                  className={authLabelClass}
                >
                  {t("auth.email")}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={`${authFieldClass} w-full pl-9`}
                    placeholder={t("auth.enterEmail")}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="password"
                  className={authLabelClass}
                >
                  {t("auth.password")}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={passwordInputRef}
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={`${authFieldClass} w-full pl-9 pr-10`}
                    placeholder={t("auth.enterPassword")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? (
                      <EyeOffIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <PasswordRequirements password={password} />
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="confirmPassword"
                  className={authLabelClass}
                >
                  {t("auth.confirmPassword")}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className={`${authFieldClass} w-full pl-9 pr-10 ${
                      confirmPassword && password !== confirmPassword
                        ? "border-red-500 focus-visible:ring-red-500"
                        : confirmPassword && password === confirmPassword
                          ? "border-green-500 focus-visible:ring-green-500"
                          : ""
                    }`}
                    placeholder={t("auth.enterConfirmPassword")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOffIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {confirmPassword && (
                  <div className="flex items-center gap-1.5 mt-2">
                    {password === confirmPassword ? (
                      <>
                        <CheckCircle2 className="h-3.5 w-3.5 text-text-success dark:text-text-success" />
                        <span className="text-xs text-text-success dark:text-text-success">
                          {t("auth.passwordsMatch")}
                        </span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-3.5 w-3.5 text-text-error dark:text-text-error" />
                        <span className="text-xs text-text-error dark:text-text-error">
                          {t("auth.passwordsDoNotMatch")}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Terms and Privacy Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms-acceptance"
                  checked={termsAccepted}
                  onCheckedChange={(checked) =>
                    setTermsAccepted(checked === true)
                  }
                  className="mt-0.5"
                />
                <label
                  htmlFor="terms-acceptance"
                  className="text-xs text-muted-foreground cursor-pointer"
                >
                  <span>{t("auth.termsAgreement")} </span>
                  Harmony's{" "}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      openViewModal("terms-of-use");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        openViewModal("terms-of-use");
                    }}
                    className="font-medium text-text-secondary dark:text-gray-100 hover:underline cursor-pointer"
                  >
                    {t("auth.termsOfService")}
                  </span>
                  <span> {t("common.and")} </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      openViewModal("privacy-policy");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        openViewModal("privacy-policy");
                    }}
                    className="font-medium text-text-secondary dark:text-gray-100 hover:underline cursor-pointer"
                  >
                    {t("auth.privacyPolicy")}
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                variant="darkPrimary"
                visuallyDisabled={isLoading || !isFormValid()}
                className={authPrimaryButtonClass}
              >
                {isLoading ? t("common.loading") : t("auth.register")}
              </Button>
            </form>

            {/* Destek çıkışı + Language/Theme Switcher */}
            <div className={authFooterClass}>
              <AuthSupportLink />
            </div>
          </div>
      <AgreementViewModal />
    </AuthStage>
  );
}
